import React, { Dispatch, Reducer, SetStateAction, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Modal } from '../../../components/containers/Modal';
import classes from './LinkEditModal.module.scss';
import { Input } from '../../../components/forms/Input';
import { RegisterOptions, useForm, useWatch, ValidateResult } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { PostEditorValidationMessages } from '../constants/validation-messages';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import {
  LinkClickTracking,
  LinkDestination,
  MakeOptional,
  PlanAnalyticsType,
  useGetLinkMetadataLazyQuery,
  UtmInput
} from '../../../models/graphql';
import { Toggle } from '../../../components/forms/Toggle';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import classNames from 'classnames';
import { ButtonViewProps } from '../../../components/actions/Button/Button.view';
import { Select } from '../../../components/forms/Select';
import { SelectItem } from '../../../components/forms/Select/Select.view';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { Loader } from '../../../components/containers/Loader';
import { linkMatcher, updateLink } from '../TextEditor/helpers/withLinks';
import { SocialProfileWithBoardsAndMentionType } from '../helpers/mapSelectedProfilesValues';
import { profilesHelpers } from '../../Settings/Profiles/Profiles.helpers';
import { capitalize } from 'lodash';
import { PostEditorLink } from '../../../models/post-editor';
import { LinkPreview } from '../LinkPreview';
import { PostEditorContext } from '../states/PostEditorProvider';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { Tooltip } from '../../../components/actions/Tooltip';
import { getExistingUtmKeys, IsKeyOfUtmInput } from '../states/reducers/link.reducer';
import { StagedLinkActions, StagedLinkState } from './StagedLinkActions';
import { StagedLinkReducer } from './StagedLink.reducer';
import { showToast } from '../../../helpers/showToast';
import { GENERIC_ERROR_MESSAGE } from '../../../constants/apollo-errors';
import { usePostLinkSettings } from '../../../hooks/usePostLinkSettings';
import { EditUtmModal } from '../../Settings/LinkTracking/EditUtmModal';
import { getInitialUtm } from '../helpers/mapStateToPostInput';
import { getUrlParams } from '../../../helpers/getUrlParams';
import { BeaconContext } from 'providers/BeaconProvider';
import { SocialNetworksEnum } from 'constants/social-networks';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export type LinkEditModalViewProps = {
  //
  setModal: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  profiles: SocialProfileWithBoardsAndMentionType[] | null;
  link: PostEditorLink;
};

export const LinkEditModalView = (props: LinkEditModalViewProps): JSX.Element => {
  const { link } = props;
  const { beacon } = useContext(BeaconContext);
  const { dispatch, editors } = useContext(PostEditorContext);
  const [editorRefs] = editors;
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { analyticsType, isTrial } = useOrganizationPlan();
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [stagedLinkState, dispatchStagedLinkState] = useReducer<Reducer<StagedLinkState, StagedLinkActions>>(
    StagedLinkReducer,
    { ...link, loading: false }
  );

  const { loading: loadingBitly, defaultBitly } = usePostLinkSettings();
  const [getLinkMetadata, getLinkMetadataResults] = useGetLinkMetadataLazyQuery();
  const { initialUrl } = link;
  const { setModal, loading, profiles } = props;
  const linkModalFormInstance = useForm({
    ...FormOptions,
    defaultValues: {
      'link-value': initialUrl,
      'link-preview': 'true',
      'redirected-url': link.destination,
      'keep-existing-utm': link.removeUtm ? 'false' : 'true',
      bitly: link.useBitly
    }
  });
  const inputRules: RegisterOptions = {
    validate: (value): ValidateResult => {
      if (!value) return PostEditorValidationMessages.linkSettingsModal.required;
      if (/\s/.test(value)) return PostEditorValidationMessages.linkSettingsModal.invalid;
      const linkMatchResult = linkMatcher(`${value}`);
      return linkMatchResult ? undefined : PostEditorValidationMessages.linkSettingsModal.invalid;
    }
  };

  const linkValueChanges = useWatch({
    control: linkModalFormInstance.control,
    name: 'link-value',
    defaultValue: initialUrl
  });

  const [isLinkEditing, setIsLinkEditing] = useState(false);
  const [utmModal, setUtmModal] = useState(false);

  const linkEditingClass = classNames(
    classes.LinkEditing,
    isLinkEditing || loading || stagedLinkState.loading ? classes.LinkEditingDisabled : ''
  );

  // On save first update the every single instance of text editor with new link values and then we dispatch a single action to update link in the state
  const onSave = () => {
    const { loading: _, ...baseLinkState } = stagedLinkState;
    for (let i = 0; i < editorRefs.current[postGroupIndex].length; i++) {
      const { editor } = editorRefs.current[postGroupIndex][i];
      updateLink(editorRefs.current[postGroupIndex], editor, baseLinkState);
    }
    dispatch({ type: 'linkUpdate', payload: { postGroupIndex, customizeIndex, link: baseLinkState } });
    setModal(false);
  };

  const supportsLinkPreview =
    profiles &&
    profiles.map(p => p.__typename).filter(t => t === 'FacebookSocialProfile' || t === 'LinkedInSocialProfile').length >
      0;

  const onLinkUpdate: ButtonViewProps['action'] = () => {
    linkModalFormInstance
      .trigger('link-value')
      .then(result => {
        if (result) {
          const formLinkValue: string = linkModalFormInstance.getValues('link-value');
          dispatchStagedLinkState({ type: 'setInitialLink', payload: formLinkValue });
        }
      })
      .catch(_e => {
        // TODO error log
        // catch validation error, since it is promise
      });
  };

  const onDiscardChanges = () => {
    linkModalFormInstance.setValue('link-value', initialUrl, { shouldValidate: true });
    dispatchStagedLinkState({
      type: 'revertInitialLink',
      payload: { initialUrl, linkMetadata: link.linkMetadata, url: link.url }
    });
  };

  const onToggleLinkPreview: ToggleAction = (newVal, complete) => {
    // linkDispatch({ type: 'togglePreview' });
    dispatchStagedLinkState({
      type: 'setLinkPreviewStatus',
      payload: newVal
    });
    complete();
  };

  const onToggleLinkRedirect: ToggleAction = (newVal, complete) => {
    dispatchStagedLinkState({
      type: 'setLinkDestination',
      payload: newVal ? LinkDestination.EXPANDED : LinkDestination.INITIAL
    });
    complete();
  };

  const onToggleKeepExisingUtm: ToggleAction = (newVal, complete) => {
    dispatchStagedLinkState({
      type: 'setKeepExistingUtmsStatus',
      payload: { value: newVal, initialUtms: newVal ? initialUtmData : {} }
    });
    complete();
  };

  const onToggleAddUtm: ToggleAction = (newVal, complete) => {
    dispatchStagedLinkState({
      type: 'setTrackingCampaignStatus',
      payload: newVal
    });
    complete();
  };

  const onSubmitCallbackUtmModal = (
    input: MakeOptional<UtmInput, 'campaign' | 'content' | 'medium' | 'source' | 'term'>
  ) => {
    dispatchStagedLinkState({ type: 'updateUtmsSettings', payload: input });
  };

  const isLinkClickTrackingGuard = (value: FormElementValueType): value is LinkClickTracking => {
    if (typeof value !== 'string') throw new Error('value is not a string');
    return value in LinkClickTracking;
  };

  const onBitlySelect: FormElementAction = (value, complete) => {
    if (!value) {
      complete(false);
      return;
    }
    if (value === LinkClickTracking.BITLY && defaultBitly === null && !loadingBitly) {
      showToast({
        severity: 'warn',
        summary: 'No Bitly Accounts',
        detail: 'You do not have any Bitly accounts connected, so this link will not have click-tracking.',
        sticky: true
      });
    }
    if (!isLinkClickTrackingGuard(value)) {
      throw new Error('value is not LinkClickTracking enum');
    }
    complete();
    dispatchStagedLinkState({ type: 'setClickTracing', payload: value });
  };

  const bitlyItems: SelectItem[] = [
    {
      label: 'Do not use Bitly',
      value: LinkClickTracking.NONE
    },
    {
      label: 'Use defaults',
      value: LinkClickTracking.BITLY
    }
  ];

  const initialUtmData = useMemo(() => getInitialUtm(stagedLinkState), [stagedLinkState]);

  const utmModalData = useMemo(
    () => ({
      ...(stagedLinkState.utmTracking ?? {
        campaign: '',
        content: '',
        medium: '',
        source: '',
        term: ''
      }),
      initialUtmData
    }),
    [initialUtmData, stagedLinkState.utmTracking]
  );

  const initialUrlized = getUrlParams(stagedLinkState.initialUrl);
  const hasRedirects =
    stagedLinkState.linkMetadata && initialUrlized && initialUrlized.href !== stagedLinkState.linkMetadata.expandedUrl;
  const existingUtmKeys =
    stagedLinkState.linkMetadata?.expandedUrl && stagedLinkState.destination === LinkDestination.EXPANDED
      ? getExistingUtmKeys(stagedLinkState.linkMetadata.expandedUrl)
      : getExistingUtmKeys(stagedLinkState.initialUrl);
  const strippedUtmFromExistingKeys = existingUtmKeys.map(k => k.replace('utm_', ''));
  const mergableUtmKeys = strippedUtmFromExistingKeys.flatMap(key => (IsKeyOfUtmInput(key) ? key : []));
  const containsExistingUtms = !!existingUtmKeys.length;
  const showLinkPreProcess = hasRedirects || containsExistingUtms;
  const disabledUtmFileds: Array<keyof UtmInput> | undefined = !stagedLinkState.removeUtm
    ? // ? UtmInputKeys.filter(k => existingUtmKeys.includes(k)).filter(k => IsKeyOfUtmInput(k))
      mergableUtmKeys
    : undefined;
  const isBitlyActive =
    stagedLinkState.linkMetadata?.isBitlink && stagedLinkState.destination !== LinkDestination.EXPANDED;

  // USE EFFECTS

  useEffect(() => {
    setIsLinkEditing(linkValueChanges && linkValueChanges !== stagedLinkState.initialUrl);
  }, [linkValueChanges, stagedLinkState.initialUrl]);

  const { error: linkMetadataError, data: linkMetadataResults } = getLinkMetadataResults;
  // if we changed the initial URL we need to fetch new link metadata
  useEffect(() => {
    if (!stagedLinkState.linkMetadata && stagedLinkState.initialUrl && !loading) {
      getLinkMetadata({
        variables: {
          urls: stagedLinkState.initialUrl
        }
      }).catch(e => {
        console.error(e);
        showToast({
          summary: 'Error fetching link metadata',
          severity: 'error',
          detail: GENERIC_ERROR_MESSAGE
        });
      });
    }
  }, [stagedLinkState.linkMetadata, stagedLinkState.initialUrl, loading, getLinkMetadata]);

  useEffect(() => {
    if (linkMetadataResults?.linkMetadata) {
      dispatchStagedLinkState({ type: 'updateLinkMetadata', payload: linkMetadataResults.linkMetadata[0] });
    }
  }, [linkMetadataResults?.linkMetadata]);

  useEffect(() => {
    if (linkMetadataError) {
      dispatchStagedLinkState({ type: 'setLoading', payload: false });
      dispatchStagedLinkState({
        type: 'updateLinkMetadata',
        payload: {
          __typename: 'LinkMetadata',
          error: 'Error Fetching Link Metadata',
          initialUrl: '', // we set both initial and expanded url to empty string here, but in the reducer, we'll set it to current value if we have error in response
          expandedUrl: '',
          domain: '',
          images: [],
          isBitlink: false, // we will set this in the reducer since we have access to the initial link there
          openGraph: null,
          twitterCard: null
        }
      });
      // TODO what to do if metadata failed ?
    }
  }, [linkMetadataError]);

  useEffect(() => {
    if (isBitlyActive) {
      dispatchStagedLinkState({ type: 'setClickTracing', payload: LinkClickTracking.NONE });
      dispatchStagedLinkState({ type: 'setTrackingCampaignStatus', payload: false });
    }
  }, [isBitlyActive]);

  useEffect(() => {
    if (defaultBitly?.bitly.connected === false) {
      showToast({
        severity: 'error',
        summary: 'Your Bitly account is disconnected',
        detail: (
          <>
            Click{' '}
            <a target={'_blank'} href={'/settings/tracking'} rel={'noreferrer'}>
              View Bitly Settings
            </a>{' '}
            to open global Link Tracking settings and reconnect your account.
          </>
        )
      });
    }
  }, [defaultBitly?.bitly.connected]);

  const displayProfileName = () => {
    if (!profiles) {
      return '';
    }
    const profileName = profilesHelpers.getSocialNetworkFromTypename(profiles[0].__typename);
    switch (profileName) {
      case SocialNetworksEnum.linkedin:
        return 'LinkedIn';
      case SocialNetworksEnum.tiktok:
        return 'TikTok';
      case SocialNetworksEnum.twitter:
        return 'X (Twitter)';
      default:
        return capitalize(profileName?.toString());
    }
  };

  const isGoogleDisabled = analyticsType === PlanAnalyticsType.BASIC_ANALYTICS;

  return (
    <>
      <Modal
        primaryButtonDisabled={isLinkEditing || loading || stagedLinkState.loading}
        onClose={() => setModal(false)}
        showCloseIcon
        size={'large'}
        closeOnClickOutside={false}
        closeOnEsc
        title={
          profiles
            ? profiles.length === 1
              ? `Link Settings for ${displayProfileName() ?? ''}`
              : 'Link Settings for all Profiles'
            : 'Link Settings'
        }
        primaryButtonTitle={'Save Changes'}
        primaryButtonAction={onSave}>
        <div className={'grid-x'}>
          <div className={'cell small-12'}>
            <Input
              label={'Original link'}
              name={'link-value'}
              size={'large'}
              formInstance={linkModalFormInstance}
              rules={inputRules}
              value={initialUrl}
            />
          </div>
        </div>
        {isLinkEditing && (
          <div className={'grid-padding-x margin-top-small-16'}>
            <div className={'grid-x grid-padding-x'}>
              <div className={'cell small-6 medium-shrink'}>
                <Button
                  expanded
                  type={'button'}
                  variant={'solid'}
                  size={'large'}
                  color={ButtonColorsEnum.primary}
                  action={onLinkUpdate}>
                  Update Link
                </Button>
              </div>
              <div className={'cell small-6 medium-shrink'}>
                <Button
                  expanded
                  type={'button'}
                  variant={'hollow'}
                  size={'large'}
                  color={ButtonColorsEnum.primary}
                  action={onDiscardChanges}>
                  Discard <span className={'show-for-medium'}>Changes</span>
                </Button>
              </div>
            </div>
          </div>
        )}
        {loading || stagedLinkState.loading ? (
          <div className={classes.LoaderWrapper}>
            <Loader variation={'dark'} size={'large'} />
          </div>
        ) : null}
        <div className={linkEditingClass}>
          <div className={classes.LabelHeadings}>Link Preview</div>
          {supportsLinkPreview ? (
            <Toggle
              formInstance={linkModalFormInstance}
              name={'link-preview'}
              rules={{}}
              label={'Enable link preview'}
              checked={!stagedLinkState.removePreview}
              action={onToggleLinkPreview}
            />
          ) : (
            <div className={linkEditingClass}>
              {profiles ? (
                <BoxNotification type={'info'}>
                  Link previews are not supported by this social network.{' '}
                  {profiles.some(
                    p => p.__typename === 'InstagramSocialProfile' || p.__typename === 'PinterestSocialProfile'
                  ) &&
                    " If your post doesn't have any media attached, we'll attempt to retrieve an image from the link and attach it to the Post instead. "}
                  <a onClick={() => beacon('article', '676', { type: 'modal' })}>Learn more</a>.
                </BoxNotification>
              ) : (
                <BoxNotification type={'info'}>
                  Link preview appearance varies by platform. Please select a Profile to preview how the link will
                  display.
                </BoxNotification>
              )}
            </div>
          )}

          {!stagedLinkState.removePreview && supportsLinkPreview ? (
            <div className={linkEditingClass}>
              <div className={'margin-top-small-16 margin-bottom-small-16'}>
                <LinkPreview
                  metadata={stagedLinkState.linkMetadata}
                  {...(profiles.length === 1
                    ? { networkMode: profilesHelpers.getSocialNetworkFromTypename(profiles[0].__typename) || undefined }
                    : {})}
                />
              </div>
              <div className={'margin-top-small-16 margin-bottom-small-16'}>
                {stagedLinkState.linkMetadata?.error ? (
                  <BoxNotification type={'warning'}>
                    A link preview isn&apos;t available because we couldn&apos;t retrieve metadata from the provided
                    URL. Please check the link for any issues.
                  </BoxNotification>
                ) : (
                  <BoxNotification type={'info'}>
                    Link previews can vary across social networks and may not appear on some. Adding media to your post
                    will replace the link preview.{' '}
                    <a onClick={() => beacon('article', '676', { type: 'modal' })}>Learn more</a>.
                  </BoxNotification>
                )}
              </div>
            </div>
          ) : null}

          {showLinkPreProcess ? (
            <div className={linkEditingClass}>
              <hr className={classes.HrSection} />
              <div className={classNames('grid-x', 'align-middle', classes.LabelHeadings)}>
                <div className={'cell shrink'}>Initial Link Handling</div>
                <div className={'cell shrink'}>
                  <Tooltip
                    target={`#tooltipPreProcess_${
                      customizeIndex !== undefined ? customizeIndex : 'group'
                    }_${postGroupIndex}`}
                    pointer
                    type={'inlined'}
                    autoZIndex={false}
                    style={{ zIndex: scssVars['z-index-overmodal'] }}>
                    <>
                      If the link you entered redirects to another URL, or contains any UTM parameters, you can tidy
                      those up here
                    </>
                  </Tooltip>
                </div>
              </div>
              {hasRedirects && stagedLinkState.linkMetadata ? (
                <div>
                  <p>
                    We&apos;ve detected that the URL redirects to a different address. Redirects can alter the
                    URL&apos;s appearance and tracking parameters.
                  </p>
                  <p>
                    Redirected link:{' '}
                    <span className={classes.RedirectedLink}>{stagedLinkState.linkMetadata.expandedUrl}</span>
                  </p>
                  <div className={classNames('margin-bottom-small-12', 'grid-x', 'align-middle')}>
                    <div className={'cell shrink'}>
                      <Toggle
                        formInstance={linkModalFormInstance}
                        name={'redirected-url'}
                        rules={{}}
                        label={'Use redirected link'}
                        checked={stagedLinkState.destination === LinkDestination.EXPANDED}
                        action={onToggleLinkRedirect}
                      />
                    </div>
                    <div className={'cell shrink'}>
                      <Tooltip
                        target={`#tooltipUseRedirect_${
                          customizeIndex !== undefined ? customizeIndex : 'group'
                        }_${postGroupIndex}`}
                        pointer
                        type={'inlined'}
                        autoZIndex={false}
                        style={{ zIndex: scssVars['z-index-overmodal'] }}>
                        <>
                          Choose whether to keep the link as you entered it, or share the destination URL that the link
                          redirects to
                        </>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              ) : null}

              {containsExistingUtms ? (
                <div className={classNames('margin-bottom-small-12', 'grid-x', 'align-middle')}>
                  <div className={'cell shrink'}>
                    <Toggle
                      formInstance={linkModalFormInstance}
                      name={'keep-existing-utm'}
                      rules={{}}
                      label={'Keep original UTM parameters'}
                      checked={!stagedLinkState.removeUtm}
                      action={onToggleKeepExisingUtm}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <Tooltip
                      target={`#tooltipKeepExistingUtm_${
                        customizeIndex !== undefined ? customizeIndex : 'group'
                      }_${postGroupIndex}`}
                      pointer
                      type={'inlined'}
                      autoZIndex={false}
                      style={{ zIndex: scssVars['z-index-overmodal'] }}>
                      If you plan to add your own UTM parameters, or prefer a cleaner URL for sharing, you can opt to
                      remove any existing UTM parameters from the link.
                    </Tooltip>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <hr className={classes.HrSection} />
          <div className={classNames(classes.LabelHeadings, { [classes.LabelHeadingsIsDisabled]: isGoogleDisabled })}>
            Google Analytics{' '}
            {(analyticsType === PlanAnalyticsType.BASIC_ANALYTICS || isTrial) && (
              <AdvancedFeature feature={AdvancedFeatures.google} />
            )}
          </div>
          {isBitlyActive ? (
            <>
              <BoxNotification type={'info'}>
                It’s not possible to use Google Analytics UTM for an existing Bitly link.
              </BoxNotification>
            </>
          ) : (
            <div
              className={classNames('grid-x align-middle', {
                [classes.InteractionContainer]: isGoogleDisabled
              })}>
              <div className={'cell shrink'}>
                <Toggle
                  formInstance={linkModalFormInstance}
                  name={'add-utm'}
                  rules={{}}
                  label={'Add UTM Campaign Tracking'}
                  checked={stagedLinkState.addUtm}
                  action={onToggleAddUtm}
                  disabled={analyticsType === PlanAnalyticsType.BASIC_ANALYTICS}
                />
              </div>
              <div className={classNames('cell small-12 medium-shrink', classes.EditUtmLabel)}>
                <div className={'margin-top-small-8 margin-top-medium-0'}>
                  <a
                    className={classNames({
                      [classes.EditUtmLabelIsDisabled]: isGoogleDisabled
                    })}
                    onClick={() => {
                      setUtmModal(true);
                    }}>
                    Edit UTM Settings
                  </a>
                </div>
              </div>
            </div>
          )}

          <hr className={classes.Hr} />
          <div className={classes.LabelHeadings}>Link Tracking</div>
          {isBitlyActive ? (
            <BoxNotification type={'info'}>
              Accurate click stats are not available for an existing Bitly link, as they will include data from all
              other places where this link has already been shared.
            </BoxNotification>
          ) : (
            <div className={'grid-x align-middle'}>
              <div className={'cell shrink'}>
                <Select
                  selectedValue={stagedLinkState.useBitly ? LinkClickTracking.BITLY : LinkClickTracking.NONE}
                  size={'large'}
                  rules={{}}
                  label={'Bitly Settings'}
                  name={'bitly'}
                  useFormInstance={linkModalFormInstance}
                  items={bitlyItems}
                  action={onBitlySelect}
                />
              </div>
              <div className={classNames('cell small-12 medium-shrink', classes.EditUtmLabel)}>
                <div className={'margin-top-small-8 margin-top-medium-20'}>
                  <a rel={'noreferrer'} target={'_blank'} href={'/settings/tracking'}>
                    View Default Bitly Settings
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
      {utmModal ? (
        <EditUtmModal
          onClose={() => setUtmModal(false)}
          campaign={utmModalData}
          disabledFields={disabledUtmFileds}
          submitCallback={onSubmitCallbackUtmModal}
        />
      ) : null}
    </>
  );
};
